import { z } from "zod";
import type { IPaginationMeta } from "./pagination";
import { EGeoStatus } from "../constants/geo.constants";


// Zod schemas for geo location
export const partnerCoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

export const geoLocationHubSchema = z.object({
  name: z.string(),
  code: z.string(),
  subCityCode: z.string(),
});

export const geoLocationSubCitySchema = z.object({
  name: z.string(),
  code: z.string(),
  cityCode: z.string(),
});

export const geoLocationCitySchema = z.object({
  name: z.string(),
  code: z.string(),
  countryCode: z.string(),
});

export const geoLocationCountrySchema = z.object({
  name: z.string(),
  code: z.string(),
});

export const geoLocationHotspotSchema = z.object({
  name: z.string(),
  code: z.string(),
  status: z.nativeEnum(EGeoStatus),
  hubCode: z.string(),
});

export const geoLocationSchema = z.object({
  coordinates: partnerCoordinatesSchema,
  hub: geoLocationHubSchema.optional(),
  subCity: geoLocationSubCitySchema.optional(),
  city: geoLocationCitySchema.optional(),
  country: geoLocationCountrySchema.optional(),
  hotspot: geoLocationHotspotSchema.optional(),
  // Legacy flat structure support (for backward compatibility)
  hubCode: z.string().optional(),
  subCityCode: z.string().optional(),
  cityCode: z.string().optional(),
  countryCode: z.string().optional(),
  formattedAddress: z.string().optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
});

// City Response DTO
export interface ICityResponseDto {
  id: string;
  name: string;
  code: string;
  countryCode: string;
  status: EGeoStatus;
  geojson?: unknown;
  metadata?: Record<string, unknown> | null;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// Sub-City Response DTO
export interface ISubCityResponseDto {
  id: string;
  name: string;
  code: string;
  cityCode: string;
  status: EGeoStatus;
  geojson?: unknown;
  metadata?: Record<string, unknown> | null;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// Hub Response DTO
export interface IHubResponseDto {
  id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  status: string;
  cityCode: string;
  subCityCode: string;
  location?: unknown; // GeoJSON Polygon
  metadata?: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// Hotspot Response DTO
export interface IHotspotResponseDto {
  id: string;
  name: string;
  status: EGeoStatus;
  postalCode?: string | null;
  hubCode: string;
  location?: unknown;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// Search Request DTOs
export interface ISearchCitiesRequest {
  page?: number;
  limit?: number;
  countryCode?: string;
  status?: EGeoStatus;
  name?: string;
  code?: string;
}

export interface ISearchSubCitiesRequest {
  page?: number;
  limit?: number;
  cityCode?: string;
  status?: EGeoStatus;
  name?: string;
  code?: string;
}

export interface ISearchHubsRequest {
  page?: number;
  limit?: number;
  cityCode?: string;
  subCityCode?: string;
  status?: EGeoStatus;
  name?: string;
  code?: string;
}

export interface ISearchHotspotsRequest {
  page?: number;
  limit?: number;
  name?: string;
  hubCode?: string;
}

// List Response DTOs
export interface ICityListResponse {
  items: ICityResponseDto[];
  meta: IPaginationMeta;
}

export interface ISubCityListResponse {
  items: ISubCityResponseDto[];
  meta: IPaginationMeta;
}

export interface IHubListResponse {
  items: IHubResponseDto[];
  meta: IPaginationMeta;
}

export interface IHotspotListResponse {
  items: IHotspotResponseDto[];
  meta: IPaginationMeta;
}

export interface ILocationCode {
  cityCode?: string
  subCityCode?: string
  hubCode?: string
}

// Partner API Response Types (inferred from Zod schemas)
export type IPartnerCoordinates = z.infer<typeof partnerCoordinatesSchema>;
export type IGeoLocationHub = z.infer<typeof geoLocationHubSchema>;
export type IGeoLocationSubCity = z.infer<typeof geoLocationSubCitySchema>;
export type IGeoLocationCity = z.infer<typeof geoLocationCitySchema>;
export type IGeoLocationCountry = z.infer<typeof geoLocationCountrySchema>;
export type IGeoLocation = z.infer<typeof geoLocationSchema>;